import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import { usePortal } from '../../../context/PortalContext';
import { todayString } from '../../../utils/appDateTime';
import { hasPermission } from '../../../utils/navPermissions';
import PortalPageShell, { PortalLoading } from '../PortalPageShell';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import ChildSelector from '../ChildSelector';
import { FileDown, Loader2, CalendarPlus } from 'lucide-react';
import {
  assertBlobKind,
  filenameFromDisposition,
  getApiErrorMessage,
  parseBlobError,
} from '../../../utils/blobDownload';
import { useToast } from '../../../hooks/use-toast';
import PortalPdfPreviewModal from '../attendance/PortalPdfPreviewModal';
import AttendanceMonthPicker from '../attendance/AttendanceMonthPicker';
import AttendanceCalendarGrid from '../attendance/AttendanceCalendarGrid';
import AttendanceDayDetailModal from '../attendance/AttendanceDayDetailModal';
import AttendanceMonthSummary from '../attendance/AttendanceMonthSummary';
import AttendanceAnalyticsPanel from '../attendance/AttendanceAnalyticsPanel';
import AttendanceExportBar from '../attendance/AttendanceExportBar';
import LeaveApplicationDialog from '../attendance/LeaveApplicationDialog';
import LeaveHistoryPanel from '../attendance/LeaveHistoryPanel';
import { ATTENDANCE_STATUS_STYLES, statusLabel } from '../attendance/attendanceStatus';
import PortalScheduleSummary from '../PortalScheduleSummary';

const now = new Date();

function parsePeriodLabel(periodLabel) {
  const match = String(periodLabel || '').trim().match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (!match) return null;
  const monthIndex = new Date(`${match[1]} 1, ${match[2]}`).getMonth();
  if (Number.isNaN(monthIndex)) return null;
  return { month: monthIndex + 1, monthYear: parseInt(match[2], 10) };
}

function resolveReportMonthParams(report) {
  if (!report) return null;
  const month = Number(report.month);
  const monthYear = report.month_year != null ? String(report.month_year) : '';
  if (month > 0 && monthYear !== '') {
    return { month, monthYear: parseInt(monthYear, 10) };
  }
  return parsePeriodLabel(report.period_label);
}

function defaultFilenameForReport(report, periodLabel) {
  if (report?.pdf_filename) return report.pdf_filename;
  const label = report?.period_label || periodLabel || 'attendance-report';
  const safe = String(label).replace(/[^a-zA-Z0-9._-]+/g, '-');
  return `attendance-${safe}.pdf`;
}

function findPublishedReport(published, reportId, periodLabel) {
  if (reportId) {
    const byId = published.find((r) => String(r.id) === String(reportId));
    if (byId) return byId;
  }
  if (periodLabel) {
    const norm = periodLabel.trim().toLowerCase();
    return published.find((r) => String(r.period_label || '').trim().toLowerCase() === norm) || null;
  }
  return null;
}

export default function PortalStudentAttendance({ parentMode = false, leavePage = false }) {
  const { user } = useAuth();
  const { activeStudentId, student, children } = usePortal();
  const { toast } = useToast();
  const canApplyLeave = hasPermission(user, 'apply_leave');
  const canViewLeaveHistory = canApplyLeave || hasPermission(user, 'view_attendance')
    || hasPermission(user, 'view_own_data')
    || hasPermission(user, 'view_child_data');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [monthYear, setMonthYear] = useState(now.getFullYear());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [published, setPublished] = useState([]);
  const [publishedLoading, setPublishedLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveHistoryKey, setLeaveHistoryKey] = useState(0);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const reportDeepLinkHandled = useRef(null);

  const showPdfPreview = useCallback((blob, filename, title) => {
    setPdfPreview({ blob, filename, title });
  }, []);

  const studentId = parentMode ? activeStudentId : student?.id;
  const displayStudent = parentMode
    ? (children || []).find((c) => c.id === activeStudentId)
    : student;

  const loadCalendar = useCallback(async () => {
    if (!studentId) {
      setCalendarData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const base = parentMode
        ? '/portal/parent/attendance/calendar'
        : '/portal/student/attendance/calendar';
      const params = {
        month,
        month_year: monthYear,
        ...(parentMode ? { student_id: studentId } : {}),
      };
      const res = await api.get(base, { params });
      setCalendarData(res.data?.data ?? null);
    } catch {
      setCalendarData(null);
    } finally {
      setLoading(false);
    }
  }, [studentId, parentMode, month, monthYear]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const todayAttendance = useMemo(() => {
    const todayStr = todayString();
    const [monthYearStr, monthStr] = [todayStr.slice(0, 4), todayStr.slice(5, 7)];
    const isCurrentMonth = month === Number(monthStr) && monthYear === Number(monthYearStr);
    if (!isCurrentMonth || !calendarData?.days) {
      return { date: todayStr, status: null };
    }
    const day = calendarData.days.find((d) => d.date === todayStr);
    const status = day?.status || null;
    return { date: todayStr, status };
  }, [calendarData, month, monthYear]);

  const loadPublished = useCallback(async () => {
    if (!studentId) {
      setPublished([]);
      setPublishedLoading(false);
      return;
    }
    setPublishedLoading(true);
    try {
      const base = parentMode
        ? '/portal/parent/attendance-reports'
        : '/portal/student/attendance-reports';
      const params = parentMode ? { student_id: studentId } : undefined;
      const res = await api.get(base, { params });
      setPublished(res.data?.data ?? []);
    } catch {
      setPublished([]);
    } finally {
      setPublishedLoading(false);
    }
  }, [studentId, parentMode]);

  useEffect(() => {
    loadPublished();
  }, [loadPublished]);

  const fetchLiveMonthPdf = useCallback(async (monthParams) => {
    const monthBase = parentMode
      ? '/portal/parent/attendance/month/pdf'
      : '/portal/student/attendance/month/pdf';
    const monthParamsApi = {
      month: monthParams.month,
      month_year: monthParams.monthYear,
      ...(parentMode && studentId ? { student_id: studentId } : {}),
    };
    return api.get(monthBase, { params: monthParamsApi, responseType: 'blob' });
  }, [parentMode, studentId]);

  const fetchPublicationPdf = useCallback(async (report) => {
    const pdfBase = parentMode
      ? `/portal/parent/attendance-reports/${report.id}/pdf`
      : `/portal/student/attendance-reports/${report.id}/pdf`;
    const params = parentMode ? { student_id: studentId } : undefined;
    return api.get(pdfBase, { params, responseType: 'blob' });
  }, [parentMode, studentId]);

  const fetchReportPdf = useCallback(async (report) => {
    const monthParams = resolveReportMonthParams(report);
    const isMonthly = (report?.mode || 'monthly') !== 'annual';

    if (isMonthly && monthParams) {
      try {
        return await fetchLiveMonthPdf(monthParams);
      } catch (liveError) {
        if (report?.id) {
          return fetchPublicationPdf(report);
        }
        throw liveError;
      }
    }

    if (report?.id) {
      try {
        return await fetchPublicationPdf(report);
      } catch (e) {
        if (e?.response?.status === 404 && monthParams) {
          return fetchLiveMonthPdf(monthParams);
        }
        throw e;
      }
    }

    if (monthParams) {
      return fetchLiveMonthPdf(monthParams);
    }

    throw new Error('Report period could not be resolved');
  }, [fetchLiveMonthPdf, fetchPublicationPdf]);

  const openReport = useCallback(async (report, { periodLabel = null } = {}) => {
    const loadingKey = report?.id || periodLabel || 'period';
    setDownloadingId(loadingKey);
    try {
      const res = await fetchReportPdf(report);
      const blob = res.data;
      const msg = await parseBlobError(blob);
      if (msg) {
        throw new Error(msg);
      }
      await assertBlobKind(blob, 'pdf');
      const filename = filenameFromDisposition(
        res.headers['content-disposition'],
        defaultFilenameForReport(report, periodLabel),
      );
      const title = report?.period_label
        ? `Attendance report — ${report.period_label}`
        : (periodLabel ? `Attendance report — ${periodLabel}` : 'Attendance report');
      showPdfPreview(blob, filename, title);
    } catch (e) {
      toast({
        title: 'Could not open report',
        description: await getApiErrorMessage(e),
        variant: 'destructive',
      });
    } finally {
      setDownloadingId(null);
    }
  }, [fetchReportPdf, showPdfPreview, toast]);

  const openReportFromPeriod = useCallback(async (periodLabel) => {
    const parsed = parsePeriodLabel(periodLabel);
    if (!parsed) {
      toast({
        title: 'Report not found',
        description: 'Could not determine which month to open.',
        variant: 'destructive',
      });
      return;
    }
    setMonth(parsed.month);
    setMonthYear(parsed.monthYear);
    await openReport({
      mode: 'monthly',
      period_label: periodLabel,
      month: parsed.month,
      month_year: String(parsed.monthYear),
    }, { periodLabel });
  }, [openReport, toast]);

  const downloadReport = useCallback(async (report) => {
    await openReport(report);
  }, [openReport]);

  const syncCalendarToReport = useCallback((report) => {
    const parsed = parsePeriodLabel(report?.period_label);
    if (parsed) {
      setMonth(parsed.month);
      setMonthYear(parsed.monthYear);
      return;
    }
    if (report?.month && report?.month_year) {
      setMonth(Number(report.month));
      setMonthYear(Number(report.month_year));
    }
  }, []);

  useEffect(() => {
    const reportId = searchParams.get('report_id');
    const periodLabel = searchParams.get('period_label');
    const deepLinkKey = reportId || periodLabel;
    if (!deepLinkKey || leavePage || publishedLoading || !studentId) return;
    if (reportDeepLinkHandled.current === deepLinkKey) return;

    const report = findPublishedReport(published, reportId, periodLabel);
    reportDeepLinkHandled.current = deepLinkKey;
    setSearchParams({}, { replace: true });

    if (report) {
      syncCalendarToReport(report);
      openReport(report);
      return;
    }

    if (periodLabel) {
      openReportFromPeriod(periodLabel);
      return;
    }

    toast({
      title: 'Report not found',
      description: 'This report may no longer be available.',
      variant: 'destructive',
    });
  }, [
    searchParams,
    published,
    publishedLoading,
    leavePage,
    studentId,
    setSearchParams,
    openReport,
    openReportFromPeriod,
    syncCalendarToReport,
    toast,
  ]);

  const handleMonthChange = ({ month: m, monthYear: y }) => {
    setMonth(m);
    setMonthYear(y);
  };

  const pageTitle = leavePage ? 'Leave Applications' : 'Attendance';
  const pageDescription = leavePage
    ? (parentMode ? 'Apply leave and track requests for your child' : 'Apply leave and track your requests')
    : (parentMode
      ? 'Attendance calendar with school events and holidays, leave, and published reports'
      : 'Your attendance calendar with school events and holidays');

  return (
    <PortalPageShell
      title={pageTitle}
      description={pageDescription}
      actions={(
        <div className="flex flex-wrap items-center gap-2">
          {parentMode ? <ChildSelector /> : null}
          {canApplyLeave ? (
            <Button type="button" size="sm" onClick={() => setLeaveOpen(true)} disabled={!studentId}>
              <CalendarPlus className="h-4 w-4 mr-2" />
              Apply leave
            </Button>
          ) : null}
        </div>
      )}
    >
      {leavePage && canViewLeaveHistory ? (
        <LeaveHistoryPanel
          parentMode={parentMode}
          studentId={studentId}
          refreshKey={leaveHistoryKey}
          onPdfReady={showPdfPreview}
        />
      ) : null}
      {!leavePage ? (
      <>
      <PortalScheduleSummary todayAttendance={todayAttendance} />
      <Card>
        <CardContent className="pt-4 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <AttendanceMonthPicker
              month={month}
              monthYear={monthYear}
              onChange={handleMonthChange}
            />
            <AttendanceExportBar
              parentMode={parentMode}
              studentId={studentId}
              month={month}
              monthYear={monthYear}
              onPdfReady={showPdfPreview}
            />
          </div>

          {loading ? <PortalLoading /> : null}

          {!loading && calendarData ? (
            <>
              <AttendanceMonthSummary summary={calendarData.summary} />
              <div className="flex flex-wrap gap-2 text-xs">
                {Object.entries(ATTENDANCE_STATUS_STYLES)
                  .filter(([k]) => k !== 'empty')
                  .map(([key]) => (
                    <span
                      key={key}
                      className={`inline-flex items-center px-2 py-0.5 rounded border ${ATTENDANCE_STATUS_STYLES[key]}`}
                    >
                      {statusLabel(key)}
                    </span>
                  ))}
              </div>
              <p className="text-xs text-muted-foreground">
                School events appear as subtitles on calendar days; click a day for details.
              </p>
              <AttendanceCalendarGrid
                days={calendarData.days || []}
                onDayClick={(day) => setSelectedDay(day)}
              />
            </>
          ) : null}
        </CardContent>
      </Card>

      <AttendanceAnalyticsPanel parentMode={parentMode} studentId={studentId} />
      {canViewLeaveHistory ? (
        <LeaveHistoryPanel
          parentMode={parentMode}
          studentId={studentId}
          refreshKey={leaveHistoryKey}
          onPdfReady={showPdfPreview}
        />
      ) : null}

      <Card>
        <CardContent className="pt-4">
          <h3 className="font-medium mb-2">Published attendance reports</h3>
          {publishedLoading ? (
            <p className="text-sm text-muted-foreground">Loading reports…</p>
          ) : published.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No published reports yet. Your school will share monthly or annual reports here.
            </p>
          ) : (
            <ul className="space-y-2">
              {published.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-border p-3 text-sm"
                >
                  <div>
                    <p className="font-medium">
                      {r.mode === 'annual' ? 'Annual' : 'Monthly'}
                      {' '}
                      — {r.period_label || 'Report'}
                    </p>
                    {r.class_label ? (
                      <p className="text-muted-foreground text-xs mt-0.5">{r.class_label}</p>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={downloadingId === r.id}
                    onClick={() => downloadReport(r)}
                  >
                    {downloadingId === r.id ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileDown className="h-4 w-4 mr-2" />
                    )}
                    Open PDF
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      </>
      ) : null}

      <AttendanceDayDetailModal
        day={selectedDay}
        open={!!selectedDay}
        onOpenChange={(o) => { if (!o) setSelectedDay(null); }}
      />

      <LeaveApplicationDialog
        open={leaveOpen}
        onOpenChange={setLeaveOpen}
        student={displayStudent}
        parentMode={parentMode}
        studentId={studentId}
        onSubmitted={() => {
          loadCalendar();
          loadPublished();
          setLeaveHistoryKey((k) => k + 1);
        }}
      />

      <PortalPdfPreviewModal
        open={!!pdfPreview}
        onOpenChange={(open) => { if (!open) setPdfPreview(null); }}
        title={pdfPreview?.title || 'PDF preview'}
        blob={pdfPreview?.blob ?? null}
        filename={pdfPreview?.filename || 'document.pdf'}
      />
    </PortalPageShell>
  );
}
